import React from "react";
import { Link, useParams } from "react-router-dom";
import { StorefrontService } from "src/app/services/StorefrontService";
import { money } from "./Basket";
import { clubTheme } from "./ClubTheme";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

const STATUS_LABEL: { [key: string]: string } = {
	closed: "Sales closed",
	off: "Not on sale",
	onsale: "On sale",
	scheduled: "On sale soon"
};

const dateLine = (iso: string) => {
	const d = new Date(iso);
	return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", weekday: "short" })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * A club's shop front.
 *
 * Everything buyable in one list, newest first, with the sale status stated on
 * every row. Showing a fixture that isn't on sale — rather than hiding it — is
 * deliberate: "on sale soon" is the answer to the question a supporter came
 * with, and an absent fixture just gets them ringing the club office.
 *
 * A function component, unlike most of this codebase. The router's params and
 * Stripe Elements both want hooks, and the shop is small and self-contained
 * enough that a wrapper per page would be more machinery than it saves.
 */
export function ShopHome() {
	const { slug = "" } = useParams();
	const [data, setData] = React.useState<any>(null);
	const [error, setError] = React.useState<string | null>(null);
	const [hasMemberships, setHasMemberships] = React.useState(false);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.get(slug);
				if (!res.ok) {
					if (live) setError("We can't find that club.");
					return;
				}
				const body = await res.json();
				if (live) setData(body);
			} catch {
				if (live) setError("We couldn't load the shop. Please try again.");
			}
		})();
		return () => { live = false; };
	}, [slug]);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.getMemberships(slug);
				if (!live || !res.ok) return;
				const body = await res.json();
				setHasMemberships((body?.memberships?.length || 0) > 0);
			} catch { /* no Join tab, no harm */ }
		})();
		return () => { live = false; };
	}, [slug]);

	if (error) {
		return (
			<div className="shop" style={clubTheme(data?.club)}>
				<div className="shop-result">
					<h2>Not found</h2>
					<p>{error}</p>
				</div>
			</div>
		);
	}

	if (!data) {
		return (
			<div className="shop" style={clubTheme(data?.club)}>
				<div className="shop-result"><span className="shop-spinner" /></div>
			</div>
		);
	}

	if (!data.ticketingEnabled) {
		return (
			<div className="shop" style={clubTheme(data?.club)}>
				<ShopHeader club={data.club} slug={slug} />
				<div className="shop-result">
					<h2>Not selling online yet</h2>
					<p>{data.club?.name} isn&rsquo;t selling tickets through Pitchbase at the moment.</p>
				</div>
			</div>
		);
	}

	const status = (value: string) => (
		<span className={`shop-status ${value}`}>{STATUS_LABEL[value] || value}</span>
	);

	// Season tickets go to their own page rather than straight into the basket:
	// each one needs a name against it, and nowhere here can ask for it.

	return (
		<div className="shop has-nav" style={clubTheme(data?.club)}>
			<ShopHeader club={data.club} slug={slug} />
			<div className="shop-inner">
				<h2 className="shop-section-title">Fixtures</h2>
				{data.fixtures?.length
					? data.fixtures.map((f: any) => (
						<Link className="shop-card" key={`f-${f.id}`} to={`/club/${slug}/fixture/${f.id}`}>
							{f.opponentBadgeUrl && <img alt="" className="shop-card-badge" src={f.opponentBadgeUrl} />}
							<div className="shop-card-body">
								<p className="shop-card-title">{f.isHome ? "v" : "away to"} {f.opponent || "TBC"}</p>
								<div className="shop-card-meta">
									{dateLine(f.kickOff)}{f.competition ? ` · ${f.competition}` : ""}
								</div>
								{status(f.saleStatus)}
							</div>
							{f.fromPricePence != null && f.saleStatus === "onsale" &&
								<div className="shop-card-right">
									<span className="shop-from">From</span>
									<span className="shop-price">{money(f.fromPricePence)}</span>
								</div>}
						</Link>
					))
					: <p className="shop-empty">No fixtures announced yet.</p>}

				{data.events?.length > 0 && <>
					<h2 className="shop-section-title">Events</h2>
					{data.events.map((e: any) => (
						<Link className="shop-card" key={`e-${e.id}`} to={`/club/${slug}/event/${e.id}`}>
							{e.logoUrl && <img alt="" className="shop-card-badge" src={e.logoUrl} />}
							<div className="shop-card-body">
								<p className="shop-card-title">{e.name}</p>
								<div className="shop-card-meta">
									{dateLine(e.start)}{e.venue ? ` · ${e.venue}` : ""}
								</div>
								{status(e.saleStatus)}
							</div>
							{e.fromPricePence != null && e.saleStatus === "onsale" &&
								<div className="shop-card-right">
									<span className="shop-from">From</span>
									<span className="shop-price">{money(e.fromPricePence)}</span>
								</div>}
						</Link>
					))}
				</>}

				{data.seasonTickets?.length > 0 && <>
					<h2 className="shop-section-title">Season tickets</h2>
					{data.seasonTickets.map((p: any) => (
						<Link className="shop-card" key={`s-${p.id}`} to={`/club/${slug}/season-tickets`}>
							<div className="shop-card-body">
								<p className="shop-card-title">{p.name}</p>
								<div className="shop-card-meta">
									{new Date(p.seasonStart).getFullYear()}/{String(new Date(p.seasonEnd).getFullYear()).slice(-2)} season
								</div>
							</div>
							<div className="shop-card-right">
								<span className="shop-price">{money(p.pricePence)}</span>
								<span className="shop-from">Buy</span>
							</div>
						</Link>
					))}
				</>}
			</div>
			<ShopNav showJoin={hasMemberships} slug={slug} />
		</div>
	);
}
